/* eslint-disable react/prop-types */
import React, {useState, useEffect} from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from '@mui/material';
import {ArrowBack, ArrowForward} from '@mui/icons-material';

const API_BASE = 'https://kops.apiireit.com/gateway';

const bankOptions = [
  '001 - Banco do Brasil S.A.',
  '033 - Banco Santander',
  '041 - Banco do Brasil',
  '104 - Caixa Econômica Federal',
  '237 - Bradesco',
  '260 - Nubank',
  '341 - Itaú Unibanco',
  '336 - C6 Bank',
  '077 - Banco Inter',
  '212 - Banco Original',
  '290 - PagBank',
  '323 - Mercado Pago',
  '197 - Stone Pagamentos',
  '260 - Nubank',
  '652 - Itaú Unibanco',
  '389 - Mercantil do Brasil',
  '070 - BRB',
  '104 - Caixa Econômica Federal',
  '001 - Banco do Brasil',
  '237 - Bradesco',
  '341 - Itaú',
  '033 - Santander',
  '077 - Inter',
  '260 - Nubank',
  '336 - C6 Bank',
  '212 - Original',
  '323 - Mercado Pago',
];

const accountTypes = ['Conta corrente', 'Conta poupança'];
const bankHelpText =
  'Se o seu banco não estiver na lista, escolha o mais próximo e informe o resto dos dados normalmente.';

const StepPagamento = ({
  authUser,
  getToken,
  banco,
  setBanco,
  tconta,
  setTconta,
  agencia,
  setAgencia,
  conta,
  setConta,
  digito,
  setDigito,
  pix,
  setPix,
  onBack,
  onNext,
}) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!banco && bankOptions.length > 0) {
      setBanco(bankOptions[0]);
    }
    if (!tconta && accountTypes.length > 0) {
      setTconta(accountTypes[0]);
    }
  }, [banco, setBanco, tconta, setTconta]);

  const handleNext = async () => {
    if (!authUser) {
      setError('Usuário não autenticado.');
      return;
    }
    if (!getToken) {
      setError('Não foi possível obter token de autenticação.');
      return;
    }
    if (!banco) {
      setError('Selecione o banco.');
      return;
    }
    if (!tconta) {
      setError('Selecione o tipo de conta.');
      return;
    }
    if (!agencia.trim()) {
      setError('Preencha a agência.');
      return;
    }
    if (!conta.trim()) {
      setError('Preencha o número da conta.');
      return;
    }
    if (!digito.trim()) {
      setError('Preencha o dígito da conta.');
      return;
    }
    if (!pix.trim()) {
      setError('Preencha a chave PIX.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const token = await getToken();
      const params = new URLSearchParams({
        user: authUser.uid,
        pix: pix.trim(),
        banco: banco.trim(),
        tconta: tconta.trim(),
        agencia: agencia.trim(),
        conta: conta.trim(),
        digito: digito.trim(),
      });

      const res = await fetch(
        `${API_BASE}/parceiros/update/bconta?${params.toString()}`,
        {
          method: 'PUT',
          headers: {Authorization: `Bearer ${token}`},
        },
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(
          text || `Erro ao salvar dados bancários (${res.status}).`,
        );
      }

      await res.json();
      onNext();
    } catch (err) {
      console.error('StepPagamento submit error:', err);
      setError(err.message || 'Erro ao salvar dados bancários.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Typography variant="h6" sx={{mb: 1, fontWeight: 800, color: '#1a1a2e'}}>
        Dados bancários
      </Typography>

      <Typography
        variant="body2"
        sx={{mb: 3, color: '#8a94a6', lineHeight: 1.6}}
      >
        Informe os dados bancários para receber pagamentos. O PIX deve ser uma
        chave ativa em seu nome.
      </Typography>

      <TextField
        label="Chave PIX"
        size="small"
        fullWidth
        value={pix}
        onChange={e => setPix(e.target.value)}
        placeholder="E-mail, telefone ou CPF/CNPJ"
        style={{marginBottom: 30, marginTop: 0}}
      />

      <Box sx={{display: 'grid', gap: 3}}>
        <FormControl fullWidth>
          <InputLabel id="select-banco-label">Banco</InputLabel>
          <Select
            labelId="select-banco-label"
            label="Banco"
            value={banco}
            onChange={e => setBanco(e.target.value)}
            size="small"
          >
            {bankOptions.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="select-tipo-conta-label">Tipo de conta</InputLabel>
          <Select
            labelId="select-tipo-conta-label"
            label="Tipo de conta"
            value={tconta}
            onChange={e => setTconta(e.target.value)}
            size="small"
          >
            {accountTypes.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Agência"
          size="small"
          fullWidth
          value={agencia}
          onChange={e => setAgencia(e.target.value)}
          placeholder="1234"
          style={{marginBottom: 0, marginTop: 0}}
        />

        <TextField
          label="Conta"
          size="small"
          fullWidth
          value={conta}
          onChange={e => setConta(e.target.value)}
          placeholder="1234567"
          style={{marginBottom: 0, marginTop: 0}}
        />

        <TextField
          label="Dígito"
          size="small"
          fullWidth
          value={digito}
          onChange={e => setDigito(e.target.value)}
          placeholder="0"
          style={{marginBottom: 30, marginTop: 0}}
        />

        <Typography variant="caption" sx={{color: '#6b7280', lineHeight: 1.6}}>
          {bankHelpText}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{mt: 3}}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 12,
          mt: 3,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {onBack && (
          <Button
            onClick={onBack}
            startIcon={<ArrowBack />}
            sx={{textTransform: 'none', color: '#6b7280'}}
          >
            Voltar
          </Button>
        )}

        <Button
          onClick={handleNext}
          endIcon={
            loading ? (
              <CircularProgress size={20} sx={{color: '#fff'}} />
            ) : (
              <ArrowForward />
            )
          }
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: '#FF6600',
            color: '#fff',
            borderRadius: '12px',
            height: 48,
            px: 3,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            ':hover': {bgcolor: '#cc4400'},
          }}
        >
          {loading ? 'Salvando...' : 'Continuar'}
        </Button>
      </Box>
    </div>
  );
};

export default StepPagamento;
